import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Empty,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import { EditOutlined, PlusOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import {
    getOrganizationManagementCenters,
    getOrganizationManagementSpecialities,
    getOrganizationManagementStaff,
    getOrganizationManagementStaffRoles,
    postOrganizationManagementStaff,
    putOrganizationManagementStaff
} from '../store/user/thunks';

const { Text, Title } = Typography;
type AnyRecord = Record<string, any>;

interface Props {
    organizationId: string;
    organizationName?: string;
    onStaffChanged?: () => void | Promise<void>;
    getOrganizationManagementCenters?: any;
    getOrganizationManagementSpecialities?: any;
    getOrganizationManagementStaff?: any;
    getOrganizationManagementStaffRoles?: any;
    postOrganizationManagementStaff?: any;
    putOrganizationManagementStaff?: any;
}

const DOCTOR_ROL_SYSTEM_ID = '1516006a-9645-4d1c-9df6-6f1bad9a5d2c';

interface Staff {
    id: string;
    dni?: string;
    fullName: string;
    firstName?: string;
    secondName?: string;
    lastName?: string;
    secondLastName?: string;
    email?: string;
    phone?: string;
    role: string;
    rolSystemId: string;
    status: string;
    licenseNumber?: string;
    specialityId?: string;
    specialityName?: string;
    centers: { id: string; name: string }[];
}

interface StaffRole {
    rolSystemId: string;
    maxUsers?: number | null;
    includedUsers: number;
    additionalLicensePrice: number;
    activeUsers: number;
    additionalLicenses: number;
    estimatedAdditionalAmount: number;
    canCreate: boolean;
    name: string;
    description?: string;
    requiresMedicalProfile: boolean;
}

interface Center {
    id: string;
    name: string;
}

interface Speciality {
    id: string;
    name: string;
    active: boolean;
}

interface StaffForm {
    status: string;
    rolSystemId: string;
    dni: string;
    firstName: string;
    secondName: string;
    lastName: string;
    secondLastName: string;
    email: string;
    phone: string;
    licenseNumber: string;
    specialityId?: string;
    centerIds: string[];
}

const pick = (source: AnyRecord | null | undefined, ...keys: string[]) => {
    if (!source) return undefined;
    for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null) return source[key];
    }
    return undefined;
};

const valuesOf = (source: AnyRecord | AnyRecord[] | null | undefined, ...keys: string[]): AnyRecord[] => {
    if (Array.isArray(source)) return source;
    const values = pick(source, ...keys);
    return Array.isArray(values) ? values : [];
};

const normalizeStaffList = (source: AnyRecord | AnyRecord[] | null | undefined): Staff[] =>
    valuesOf(source, 'items', 'data', 'staff', 'Staff').map(item => ({
        id: String(pick(item, 'id', 'Id') || ''),
        dni: pick(item, 'dni', 'Dni'),
        fullName: String(pick(item, 'fullName', 'FullName') || 'Personal'),
        firstName: pick(item, 'firstName', 'FirstName'),
        secondName: pick(item, 'secondName', 'SecondName'),
        lastName: pick(item, 'lastName', 'LastName'),
        secondLastName: pick(item, 'secondLastName', 'SecondLastName'),
        email: pick(item, 'email', 'Email'),
        phone: pick(item, 'phone', 'Phone'),
        role: String(pick(item, 'role', 'Role') || 'Sin rol'),
        rolSystemId: String(pick(item, 'rolSystemId', 'RolSystemId') || ''),
        status: String(pick(item, 'status', 'Status') || 'ACTIVO').toUpperCase(),
        licenseNumber: pick(item, 'licenseNumber', 'LicenseNumber'),
        specialityId: pick(item, 'specialityId', 'SpecialityId')
            ? String(pick(item, 'specialityId', 'SpecialityId'))
            : undefined,
        specialityName: pick(item, 'specialityName', 'SpecialityName'),
        centers: valuesOf(pick(item, 'centers', 'Centers')).map(center => ({
            id: String(pick(center, 'id', 'Id') || ''),
            name: String(pick(center, 'name', 'Name') || 'Centro')
        }))
    })).filter(item => item.id);

const emptyForm = (rolSystemId = ''): StaffForm => ({
    status: 'ACTIVO',
    rolSystemId,
    dni: '',
    firstName: '',
    secondName: '',
    lastName: '',
    secondLastName: '',
    email: '',
    phone: '',
    licenseNumber: '',
    specialityId: undefined,
    centerIds: []
});

const OrganizationStaffManagement: React.FC<Props> = props => {
    const [staff, setStaff] = useState<Staff[]>([]);
    const [roles, setRoles] = useState<StaffRole[]>([]);
    const [centers, setCenters] = useState<Center[]>([]);
    const [specialities, setSpecialities] = useState<Speciality[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState<StaffForm>(emptyForm());
    const [editingStaff, setEditingStaff] = useState<Staff | null>(null);

    const replaceStaffCollection = (source: AnyRecord | AnyRecord[] | null | undefined) => {
        const nextStaff = normalizeStaffList(source);
        setStaff(nextStaff);
        setRoles(currentRoles => currentRoles.map(role => {
            const activeUsers = nextStaff.filter(item =>
                item.status === 'ACTIVO' && item.rolSystemId === role.rolSystemId
            ).length;
            const additionalLicenses = Math.max(activeUsers - role.includedUsers, 0);
            const canCreate = role.maxUsers == null
                || role.maxUsers <= 0
                || activeUsers < role.maxUsers;
            return {
                ...role,
                activeUsers,
                additionalLicenses,
                estimatedAdditionalAmount: additionalLicenses * role.additionalLicensePrice,
                canCreate
            };
        }));
    };

    const load = async () => {
        setLoading(true);
        try {
            const [staffResponse, rolesResponse, centersResponse, specialitiesResponse] = await Promise.all([
                props.getOrganizationManagementStaff?.(props.organizationId),
                props.getOrganizationManagementStaffRoles?.(props.organizationId),
                props.getOrganizationManagementCenters?.(props.organizationId),
                props.getOrganizationManagementSpecialities?.(props.organizationId)
            ]);

            setStaff(normalizeStaffList(staffResponse));

            setRoles(valuesOf(rolesResponse, 'items', 'data', 'roles', 'Roles').map(item => ({
                rolSystemId: String(pick(item, 'rolSystemId', 'RolSystemId') || ''),
                maxUsers: pick(item, 'maxUsers', 'MaxUsers') == null ? null : Number(pick(item, 'maxUsers', 'MaxUsers')),
                includedUsers: Number(pick(item, 'includedUsers', 'IncludedUsers') || 0),
                additionalLicensePrice: Number(pick(item, 'additionalLicensePrice', 'AdditionalLicensePrice') || 0),
                activeUsers: Number(pick(item, 'activeUsers', 'ActiveUsers') || 0),
                additionalLicenses: Number(pick(item, 'additionalLicenses', 'AdditionalLicenses') || 0),
                estimatedAdditionalAmount: Number(pick(item, 'estimatedAdditionalAmount', 'EstimatedAdditionalAmount') || 0),
                canCreate: pick(item, 'canCreate', 'CanCreate') == null
                    ? true
                    : Boolean(pick(item, 'canCreate', 'CanCreate')),
                name: String(pick(item, 'name', 'Name') || 'Rol'),
                description: pick(item, 'description', 'Description'),
                requiresMedicalProfile: Boolean(pick(item, 'requiresMedicalProfile', 'RequiresMedicalProfile'))
            })).filter(item => item.rolSystemId));

            setCenters(valuesOf(centersResponse, 'items', 'data', 'centers', 'Centers').map(item => ({
                id: String(pick(item, 'id', 'Id') || ''),
                name: String(pick(item, 'name', 'Name') || 'Centro')
            })).filter(item => item.id));

            setSpecialities(valuesOf(specialitiesResponse, 'items', 'data', 'specialities', 'Specialities').map(item => ({
                id: String(pick(item, 'specialityId', 'SpecialityId', 'id', 'Id') || ''),
                name: String(pick(item, 'name', 'Name') || 'Especialidad'),
                active: Boolean(pick(item, 'isAssociated', 'IsAssociated'))
                    && String(pick(item, 'status', 'Status') || 'ACTIVO').toUpperCase() === 'ACTIVO'
            })).filter(item => item.id && item.active));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.organizationId]);

    const activeStaffByRole = useMemo(() => staff
        .filter(item => item.status === 'ACTIVO')
        .reduce<Record<string, number>>((result, item) => {
            result[item.rolSystemId] = (result[item.rolSystemId] || 0) + 1;
            return result;
        }, {}), [staff]);

    const activeUsersAfterChange = (role: StaffRole, status: string, currentStaff: Staff | null = editingStaff) => {
        const currentActiveUsers = activeStaffByRole[role.rolSystemId] || 0;
        const currentStaffContribution = currentStaff?.status === 'ACTIVO'
            && currentStaff.rolSystemId === role.rolSystemId
            ? 1
            : 0;
        const activeUsersBefore = Math.max(currentActiveUsers - currentStaffContribution, 0);
        return activeUsersBefore + (status === 'ACTIVO' ? 1 : 0);
    };

    const roleHasCapacity = (role: StaffRole, status = 'ACTIVO', currentStaff: Staff | null = editingStaff) => {
        if (role.maxUsers == null || role.maxUsers <= 0 || status !== 'ACTIVO') return false;
        // La plaza actual del usuario editado no debe bloquear su propia
        // conservación de rol. Para un alta nueva, el valor representa los
        // usuarios activos antes de agregar la nueva plaza.
        const activeUsersBeforeChange = activeUsersAfterChange(role, 'INACTIVO', currentStaff);
        return activeUsersBeforeChange >= role.maxUsers;
    };

    const getAdditionalLicenseSummary = (role: StaffRole, status = 'ACTIVO', currentStaff: Staff | null = editingStaff) => {
        const activeUsersAfter = activeUsersAfterChange(role, status, currentStaff);
        const additionalLicensesAfter = Math.max(activeUsersAfter - role.includedUsers, 0);
        const currentAdditionalLicenses = Math.max(role.additionalLicenses || 0, 0);
        const additionalLicensesAdded = Math.max(additionalLicensesAfter - currentAdditionalLicenses, 0);
        return {
            activeUsersAfter,
            additionalLicensesAfter,
            additionalLicensesAdded,
            additionalAmount: additionalLicensesAdded * role.additionalLicensePrice
        };
    };

    const update = (field: keyof StaffForm, value: any) => setForm(current => ({ ...current, [field]: value }));

    const selectedRole = roles.find(item => item.rolSystemId === form.rolSystemId);
    const isDoctor = form.rolSystemId === DOCTOR_ROL_SYSTEM_ID || !!selectedRole?.requiresMedicalProfile;

    const canCreateAnyUser = roles.some(role => !roleHasCapacity(role, 'ACTIVO', null));

    const openCreate = () => {
        const firstAvailableRole = roles.find(role => !roleHasCapacity(role, 'ACTIVO', null));
        if (!firstAvailableRole) {
            message.warning('Todos los roles alcanzaron su capacidad máxima. Inactive o bloquee un usuario activo, o amplíe el contrato.');
            return;
        }
        setEditingStaff(null);
        setForm({ ...emptyForm(firstAvailableRole.rolSystemId), centerIds: centers.length === 1 ? [centers[0].id] : [] });
        setModalOpen(true);
    };

    const openEdit = (item: Staff) => {
        setEditingStaff(item);
        setForm({
            status: item.status || 'ACTIVO',
            rolSystemId: item.rolSystemId,
            dni: item.dni || '',
            firstName: item.firstName || '',
            secondName: item.secondName || '',
            lastName: item.lastName || '',
            secondLastName: item.secondLastName || '',
            email: item.email || '',
            phone: item.phone || '',
            licenseNumber: item.licenseNumber || '',
            specialityId: item.specialityId,
            centerIds: item.centers.map(center => center.id)
        });
        setModalOpen(true);
    };

    const save = async () => {
        if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
            message.warning('Complete nombres, apellidos y email.');
            return;
        }
        if (!form.centerIds.length) {
            message.warning('Seleccione al menos un centro médico.');
            return;
        }
        if (!form.rolSystemId) {
            message.warning('Seleccione el rol del usuario.');
            return;
        }
        if (selectedRole && roleHasCapacity(selectedRole, form.status, editingStaff)) {
            message.warning(`El plan alcanzó el máximo de ${selectedRole.maxUsers} usuarios para el rol ${selectedRole.name}. Inactive o bloquee un usuario activo antes de continuar.`);
            return;
        }
        if (isDoctor && (!form.specialityId || !form.licenseNumber.trim())) {
            message.warning('El médico requiere especialidad y licencia.');
            return;
        }
        const saveStaff = async () => {
            setSaving(true);
            try {
                const request = {
                    Id: editingStaff?.id || null,
                    Status: form.status,
                    RolSystemId: form.rolSystemId,
                    Role: selectedRole?.name,
                    Dni: form.dni || 'N/A',
                    FirstName: form.firstName,
                    SecondName: form.secondName || null,
                    LastName: form.lastName,
                    SecondLastName: form.secondLastName || null,
                    Email: form.email,
                    Phone: form.phone || null,
                    LicenseNumber: isDoctor ? form.licenseNumber : null,
                    SpecialityId: isDoctor ? form.specialityId : null,
                    CenterIds: form.centerIds
                };
                const response = editingStaff
                    ? await props.putOrganizationManagementStaff?.(props.organizationId, editingStaff.id, request)
                    : await props.postOrganizationManagementStaff?.(props.organizationId, request);
                if (response) {
                    // POST/PUT devuelve la colección completa de /staff.
                    // Reemplazamos el estado local sin repetir GET.
                    replaceStaffCollection(response);
                    setModalOpen(false);
                    setEditingStaff(null);
                    await props.onStaffChanged?.();
                }
            } finally {
                setSaving(false);
            }
        };

        const licenseSummary = selectedRole
            ? getAdditionalLicenseSummary(selectedRole, form.status, editingStaff)
            : null;
        const requiresAdditionalLicenseConfirmation = !!licenseSummary
            && form.status === 'ACTIVO'
            && licenseSummary.additionalLicensesAdded > 0;

        if (requiresAdditionalLicenseConfirmation && licenseSummary && selectedRole) {
            Modal.confirm({
                title: 'Confirmar licencia adicional',
                content: (
                    <Space direction="vertical" size={6}>
                        <Text>Esta acción activará {licenseSummary.additionalLicensesAdded} licencia(s) adicional(es) para el rol <Text strong>{selectedRole.name}</Text>.</Text>
                        <Text>El costo será de <Text strong>{licenseSummary.additionalAmount.toFixed(2)} USD</Text> por período y se incluirá en el siguiente cobro del contrato.</Text>
                        <Text type="secondary">Usuarios activos después del cambio: {licenseSummary.activeUsersAfter}. Licencias adicionales activas: {licenseSummary.additionalLicensesAfter}.</Text>
                    </Space>
                ),
                okText: editingStaff ? 'Confirmar activación' : 'Confirmar y crear usuario',
                cancelText: 'Cancelar',
                onOk: saveStaff
            });
            return;
        }

        await saveStaff();
    };

    const columns = [
        {
            title: 'Personal',
            key: 'person',
            render: (_: unknown, item: Staff) => <Space direction="vertical" size={0}><Text strong>{item.fullName}</Text><Text type="secondary">{item.email || 'Sin correo'}</Text></Space>
        },
        { title: 'Rol', dataIndex: 'role', render: (value: string) => <Tag color="blue">{value}</Tag> },
        { title: 'Especialidad', dataIndex: 'specialityName', render: (value: string) => value || 'No aplica' },
        { title: 'Centros', key: 'centers', render: (_: unknown, item: Staff) => item.centers.map(center => center.name).join(', ') || 'Sin centro' },
        { title: 'Estado', dataIndex: 'status', render: (value: string) => <Tag color={value === 'ACTIVO' ? 'green' : value === 'BLOQUEADO' ? 'orange' : 'default'}>{value}</Tag> },
        {
            title: 'Acciones',
            key: 'actions',
            align: 'right' as const,
            render: (_: unknown, item: Staff) => <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(item)}>Editar acceso</Button>
        }
    ];

    return <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card>
            <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                <div>
                    <Title level={3} style={{ margin: 0 }}>Usuarios de la organización</Title>
                    <Text type="secondary">Personal de {props.organizationName || 'la organización'} con roles y centros asignados.</Text>
                </div>
                <Space wrap>
                    <Button icon={<ReloadOutlined />} onClick={load}>Actualizar</Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openCreate}
                        disabled={!centers.length || !roles.length || !canCreateAnyUser}
                        title={!canCreateAnyUser ? 'Todos los roles alcanzaron su capacidad máxima' : undefined}
                    >Nuevo usuario</Button>
                </Space>
            </Space>
        </Card>

        {!roles.length && <Alert type="warning" showIcon message="El contrato activo no tiene roles habilitados para registrar usuarios." />}
        {roles.filter(role => role.maxUsers != null && role.maxUsers > 0 && (activeStaffByRole[role.rolSystemId] || 0) >= (role.maxUsers || 0)).map(role => <Alert key={role.rolSystemId} type="warning" showIcon message={`El plan alcanzó el máximo de ${role.maxUsers} usuarios para el rol ${role.name}.`} />)}
        {!centers.length && <Alert type="warning" showIcon message="Registre al menos una sucursal antes de crear personal clínico." />}

        <Row gutter={[16, 16]}>
            <Col xs={12} md={6}><Card><Statistic title="Personal registrado" value={staff.length} /></Card></Col>
            {roles.slice(0, 4).map(role => <Col xs={12} md={6} key={role.rolSystemId}>
                <Card>
                    <Statistic title={role.name} value={activeStaffByRole[role.rolSystemId] || 0} suffix={role.maxUsers == null || role.maxUsers === 0 ? '/ ilimitados' : `/ ${role.maxUsers}`} />
                    <div><Text type="secondary">Incluidas: {role.includedUsers} · Adicional: ${role.additionalLicensePrice.toFixed(2)}</Text></div>
                    <div><Text type="secondary">Licencias adicionales activas: {role.additionalLicenses} · Próximo cargo estimado: ${role.estimatedAdditionalAmount.toFixed(2)}</Text></div>
                </Card>
            </Col>)}
            <Col xs={12} md={6}><Card><Statistic title="Roles del contrato" value={roles.length} /></Card></Col>
        </Row>

        <Card title={<Space><UserOutlined /> Personal registrado</Space>}>
            {loading ? <div style={{ textAlign: 'center', padding: 36 }}><Spin /></div> : staff.length ? <Table rowKey="id" columns={columns} dataSource={staff} pagination={{ pageSize: 8 }} /> : <Empty description="No hay usuarios registrados" />}
        </Card>

        <Modal title={`${editingStaff ? 'Editar' : 'Nuevo'} ${selectedRole?.name || 'usuario'}`} open={modalOpen} onCancel={() => { setModalOpen(false); setEditingStaff(null); }} onOk={save} okText={editingStaff ? 'Guardar cambios' : 'Crear usuario'} cancelText="Cancelar" confirmLoading={saving} width={760}>
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
                <Select
                    value={form.rolSystemId || undefined}
                    onChange={value => update('rolSystemId', value)}
                    style={{ width: '100%' }}
                    placeholder="Seleccione el rol del usuario"
                    options={roles.map(item => ({
                        value: item.rolSystemId,
                        label: item.name,
                        title: item.description,
                        disabled: !editingStaff && roleHasCapacity(item, 'ACTIVO', null)
                    }))}
                />
                {selectedRole && <Alert
                    type="info"
                    showIcon
                    message={`Licencias del rol: ${selectedRole.includedUsers} incluidas · ${selectedRole.additionalLicensePrice.toFixed(2)} USD por licencia adicional`}
                    description={selectedRole.maxUsers == null || selectedRole.maxUsers === 0
                        ? 'Capacidad máxima ilimitada.'
                        : `Capacidad máxima: ${selectedRole.maxUsers} usuarios activos. Activos después del cambio: ${activeUsersAfterChange(selectedRole, form.status)}.`}
                />}
                {editingStaff && <Select value={form.status} onChange={value => update('status', value)} style={{ width: '100%' }} options={[{ value: 'ACTIVO', label: 'Activo' }, { value: 'INACTIVO', label: 'Inactivo' }, { value: 'BLOQUEADO', label: 'Bloqueado' }]} />}
                <Row gutter={[12, 12]}>
                    <Col xs={24} md={12}><Text type="secondary">Primer nombre *</Text><Input value={form.firstName} onChange={event => update('firstName', event.target.value)} /></Col>
                    <Col xs={24} md={12}><Text type="secondary">Segundo nombre</Text><Input value={form.secondName} onChange={event => update('secondName', event.target.value)} /></Col>
                    <Col xs={24} md={12}><Text type="secondary">Primer apellido *</Text><Input value={form.lastName} onChange={event => update('lastName', event.target.value)} /></Col>
                    <Col xs={24} md={12}><Text type="secondary">Segundo apellido</Text><Input value={form.secondLastName} onChange={event => update('secondLastName', event.target.value)} /></Col>
                    <Col xs={24} md={8}><Text type="secondary">Identificación</Text><Input value={form.dni} onChange={event => update('dni', event.target.value)} /></Col>
                    <Col xs={24} md={8}><Text type="secondary">Email *</Text><Input type="email" value={form.email} onChange={event => update('email', event.target.value)} /></Col>
                    <Col xs={24} md={8}><Text type="secondary">Teléfono</Text><Input value={form.phone} onChange={event => update('phone', event.target.value)} /></Col>
                    {isDoctor && <>
                        <Col xs={24} md={12}><Text type="secondary">Licencia *</Text><Input value={form.licenseNumber} onChange={event => update('licenseNumber', event.target.value)} /></Col>
                        <Col xs={24} md={12}><Text type="secondary">Especialidad *</Text><Select value={form.specialityId} onChange={value => update('specialityId', value)} style={{ width: '100%' }} placeholder="Seleccione una especialidad" options={specialities.map(item => ({ value: item.id, label: item.name }))} /></Col>
                    </>}
                    <Col xs={24}><Text type="secondary">Sucursales *</Text><Select mode="multiple" value={form.centerIds} onChange={value => update('centerIds', value)} style={{ width: '100%' }} placeholder="Seleccione las sucursales" options={centers.map(center => ({ value: center.id, label: center.name }))} /></Col>
                </Row>
                <Alert type="info" showIcon message={editingStaff ? 'Los cambios de rol, centros y estado se validan contra el contrato vigente. Al inactivar no se elimina el historial del usuario.' : 'El usuario se crea con el rol seleccionado del contrato y se envía el acceso para configurar su contraseña. El horario del médico se configura después desde Médicos y horarios.'} />
            </Space>
        </Modal>
    </Space>;
};

export default connect(null, {
    getOrganizationManagementCenters,
    getOrganizationManagementSpecialities,
    getOrganizationManagementStaff,
    getOrganizationManagementStaffRoles,
    postOrganizationManagementStaff,
    putOrganizationManagementStaff
})(OrganizationStaffManagement);
